import { parseTimestamp } from '@/utils/dateDisplay';
// `Model` from its own module, not the `@/models` barrel — see the note in `index.ts`.
import { Model } from './model';

export interface NetworkExport {
  identifier: string;
  investigationIdentifier: string;
  surveyIdentifier: string[];
  creatorIdentifier: string;
  editorIdentifier: string;
  key: string;
  contentType: string;
  storedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  creatorBase64Identifier: string;
  creatorAvatar: string;
  creatorNames: string;
  exportFormNames: string[];
  error: ExportError | null;
  options?: ExportOptions | null;
}

export interface ExportOptions {
  formIdentifiers?: string[];
  caseIdentifiers?: string[];
  choiceLabels?: boolean;
  format?: string;
  includeArchive?: boolean;
  files?: boolean;
  metadata?: boolean;
  header?: string;
  timezone?: string;
  separateMatrixSheets?: boolean;
  exportColumnHeadersForMatrix?: 'columns' | 'rows';
  period?: {
    start: number;
    end: number;
  };
}

export interface PostExport {
  formIdentifiers: string[];
  caseIdentifiers?: string[];
  choiceLabels: boolean;
  format: string;
  period?: {
    start: number;
    end: number;
  },
  includeArchive: boolean;
  files?: boolean;
  metadata?: boolean;
  shiftDates?: boolean;
  header?: string;
  timezone?: string;
  separateMatrixSheets?: boolean;
  exportColumnHeadersForMatrix?: 'columns' | 'rows';
}

export interface ExportColumnHeader {
  label: string;
  value: string;
}

export interface PostExportResponse {
  identifier?: string;
}

export interface ExportError {
  message: string,
  name: string,
  stack: string,
}

interface IExport {
  identifier: string;
  contentType: string;
  createdAt: Date;
  storedAt?: Date;
  creatorAvatar: string;
  creatorNames: string;
  fileKey: string;
  exportFormNames: string | string[];
  error: ExportError | null;
  options: ExportOptions | null | undefined;
}

export class ExportModel extends Model {
  identifier: string;
  contentType: string;
  createdAt: Date;
  storedAt?: Date;
  avatar: string;
  creatorNames: string;
  fileKey: string;
  exportFormNames: string | string[];
  preparing?: boolean;
  error: ExportError | null;
  options: ExportOptions | null | undefined;

  constructor({
    identifier,
    contentType,
    createdAt,
    storedAt,
    creatorAvatar,
    creatorNames,
    fileKey,
    exportFormNames,
    error,
    options
  }: IExport) {
    super();
    this.identifier = identifier;
    this.contentType = contentType;
    this.createdAt = createdAt;
    this.storedAt = storedAt;
    this.avatar = creatorAvatar;
    this.creatorNames = creatorNames;
    this.fileKey = fileKey;
    this.exportFormNames = exportFormNames;
    this.error = error;
    this.options = options;
  }

  static mapFromBackend(networkExport: NetworkExport) {
    return new ExportModel({
      identifier: networkExport.identifier,
      contentType:
        networkExport.contentType.includes('csv') ?
          networkExport.contentType.split('/')[1] :
          networkExport.contentType.includes('sheet') ? 'xlsx' : '',
      createdAt: parseTimestamp(networkExport.createdAt).toJSDate(),
      storedAt: networkExport.storedAt ? parseTimestamp(networkExport.storedAt).toJSDate() : undefined,
      creatorAvatar: networkExport.creatorAvatar,
      creatorNames: networkExport.creatorNames,
      fileKey: networkExport.key,
      exportFormNames: networkExport.exportFormNames.length > 1 ? 'Multiple forms' : networkExport.exportFormNames[0],
      error: networkExport.error,
      options: networkExport.options
    });
  }
}
